"use client";

import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { useMemo } from "react";
import { useFormContext } from "react-hook-form";

import { NoxusFormLabel } from "@/components/Form/NoxusFormLabel";

import { NoxusFormHelperText } from "@/components/Form/NoxusFormHelperText";
import { Combobox } from "@/components/shadcn/combobox";
import { BindableInput } from "./BindableInput";
import { cn } from "@/lib/utils";

/**
 * V2 renderer for `display=ConfigBindableSelect`. A searchable single-select
 * dropdown (Combobox) whose options are usually populated dynamically by the
 * node's `get_config` (integration dropdowns: channels, folders, projects…).
 * It is *also* bindable: the `{}` affordance from {@link BindableInput} lets
 * the user bind the whole field to an upstream variable instead of picking
 * from the list.
 *
 * Literal mode renders the dropdown; variable mode (value is exactly
 * `:var[<ref>]`) renders the bound chip — both handled by BindableInput.
 */

type Option = { value: string; label: string };

function normalizeValues(values: unknown): Option[] {
  if (!Array.isArray(values)) return [];
  return values
    .map((v): Option | null => {
      if (typeof v === "string") return { value: v, label: v };
      if (v && typeof v === "object") {
        const value = (v as { value?: unknown }).value;
        const label = (v as { label?: unknown }).label;
        if (typeof value === "string") {
          return { value, label: typeof label === "string" ? label : value };
        }
      }
      return null;
    })
    .filter((o): o is Option => o !== null);
}

export function BindableSelectField({
  nodeId,
  fieldKey,
  label,
  tooltip,
  description,
  placeholder,
  values,
  disabled,
  isRequired,
  showOptionalLabel,
  error,
}: {
  nodeId: string;
  fieldKey: string;
  label: string;
  tooltip?: string;
  description?: string;
  placeholder?: string;
  values?: unknown;
  disabled?: boolean;
  isRequired?: boolean;
  showOptionalLabel?: boolean;
  error?: string;
}) {
  const form = useFormContext();
  const path = `${nodeId}.${fieldKey}`;
  const raw = form.watch(path);
  const value: string = typeof raw === "string" ? raw : "";
  const options = useMemo(() => {
    const normalized = normalizeValues(values);
    // Keep a saved value visible while options are loading or failed to load,
    // otherwise the selection looks cleared.
    if (value && !normalized.some((o) => o.value === value)) {
      return [{ value, label: value }, ...normalized];
    }
    return normalized;
  }, [values, value]);
  const resolvedLabel = label || humanize(fieldKey);
  const invalid = !!error;

  const literal = (
    <Combobox
      options={options}
      value={value || undefined}
      placeholder={placeholder ?? "Select…"}
      searchPlaceholder="Search…"
      emptyText="No options available"
      onValueChange={(v) =>
        form.setValue(path, v ?? "", { shouldDirty: true, shouldTouch: true })
      }
      disabled={disabled}
      hasPortal
      className="h-8"
    />
  );

  return (
    <div className="flex flex-col gap-1">
      <NoxusFormLabel
        label={resolvedLabel}
        tooltip={tooltip}
        name={fieldKey}
        noMargin
        isDisabled={disabled}
        isRequired={isRequired}
        showOptionalLabel={showOptionalLabel}
      />
      <div
        className={cn(
          invalid && "rounded-md ring-1 ring-inset ring-warning-400/70"
        )}
      >
        <BindableInput
          nodeId={nodeId}
          value={value}
          dataType="str"
          inputName={fieldKey}
          disabled={disabled}
          renderLiteral={literal}
          onChange={(next) =>
            form.setValue(path, next, {
              shouldDirty: true,
              shouldTouch: true,
            })
          }
        />
      </div>
      {description && !error ? (
        <NoxusFormHelperText>{description}</NoxusFormHelperText>
      ) : null}
      {error ? (
        <NoxusAlert look="banner" title={error} variant="warning" size="xs" />
      ) : null}
    </div>
  );
}

function humanize(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}
